import React, { FC, memo, useCallback, useMemo } from 'react';
import { Button, Icon, TextBadge, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { CVT, CVT_STATUS_MAP, CvtStatus } from '@packages/models/api/cvt';
import { analyticsService, cvtService } from '@web/services/singletons';
import { DATE_FORMAT } from '@web/config/constants';
import { GetCVTParams, useCvt } from '@packages/contexts/cvt';
import { getDisplayDate, getDisplayValue } from '@web/utils';
import { Link, useLocation } from 'react-router-dom';
import { ProgressBar } from 'react-bootstrap';
import { StyleSheet } from 'react-native';
import { TextBadgeWithCount } from '@web/components/text-badge-with-count';
import { useQuery } from '@web/core/hooks';
import { useTranslation } from 'react-i18next';
import { useUpdateQueryParams } from '../hook/useQueryParams';
import {
    SORT_DIRECTION,
    TableCell,
    TableHeader,
    TablePagination,
    TableRenderer,
    TableRow,
} from '@web/components/table';
import colors from '@packages/core/styles/colors';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

const useStyles = createUseStyles({
    badgeCell: {
        alignItems: 'center',
        display: 'flex',
        height: '60px',
        justifyContent: 'left',
    },
    fileCell: {
        '&:hover': {
            textDecoration: 'underline !important',
        },
    },
    actionIcons: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        padding: '10px',
    },
    progress: {
        width: '90%',
    },
    tooltip: {
        marginTop: 5,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            maxWidth: 272,
            padding: '3px 8px',
            textAlign: 'left',
        },
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },
    outerTableEmpty: {
        height: '50vh',
        minHeight: 'auto',
    },
});

const styles = StyleSheet.create({
    tableHeader: {
        fontWeight: '600',
        fontSize: 15,
    },
    emptyStateDescriptionTitle: {
        display: 'flex',
        fontSize: 21,
        fontWeight: '600',
        justifyContent: 'center',
        lineHeight: 32,
    },
    typographyStyle: {
        padding: 0,
    },
    errorBadge: {
        paddingRight: 6,
        marginLeft: 5,
    },
});
interface DiagnosticIdCellProps {
    diagnosticId: string;
    status: CvtStatus;
    isPending: boolean;
    path: string;
}

const useTableHeaderData = () => {
    const { t } = useTranslation();
    return [
        {
            key: 'diagnosticId',
            label: t('cvt:table.columns.diagnosticId', 'SOA Diagnostic ID'),
            sortable: false,
            width: '30%',
        },
        {
            key: 'symptoms',
            label: t('cvt:table.columns.symptoms', 'Diagnosed Symptoms'),
            sortable: false,
            width: '10%',
        },
        { key: 'createdBy', label: t('cvt:table.columns.createdBy', 'Created By'), sortable: false, width: '15%' },
        {
            key: 'jobSubmittedTimestamp',
            label: t('cvt:table.columns.created', 'Created'),
            sortable: true,
            width: '15%',
        },
        { key: 'status', label: t('cvt:table.columns.status', 'Status'), sortable: true, width: '15%' },
        { key: 'actions', label: t('cvt:table.columns.actions', 'Actions'), sortable: false, width: '15%' },
    ];
};

export const ErrorBadge: FC<{ errorType: string }> = ({ errorType }) => {
    const isNoResultError = errorType === 'NoResult';

    const formattedErrorType = useMemo(() => errorType.replace(/([a-z])([A-Z])/g, '$1 $2'), [errorType]);

    const iconName = isNoResultError ? 'check-circle-hollow' : 'error';
    const iconColor = isNoResultError ? 'greenOne' : 'redOne';
    const badgeVariant = isNoResultError ? 'green' : 'red';

    return (
        <TextBadge
            icon={<Icon name={iconName} size={18} color={iconColor} />}
            variant={badgeVariant}
            style={styles.errorBadge}
            typographyProps={{ numberOfLines: 1 }}
        >
            {formattedErrorType}
        </TextBadge>
    );
};

const BadgeComponent: FC<{ data: CVT }> = ({ data }) => {
    const classes = useStyles();
    const status = CVT_STATUS_MAP[data.status];
    const symptoms = data.sbrResponseData?.symptomTitle || [];
    const errorType = data.sbrResponseData?.errorType;

    if (status === 'Pending') {
        return <ProgressBar className={classes.progress} animated now={100} />;
    }
    if (status === 'Failed') {
        return <Icon name="x-close" size={24} />;
    }
    if (!symptoms.length && status === 'Completed' && errorType) {
        return <ErrorBadge errorType={errorType} />;
    }

    if (!symptoms.length) {
        return getDisplayValue(null);
    }

    return (
        <TextBadgeWithCount
            items={symptoms}
            tooltipClassName={classes.tooltip}
            tooltipId={`info-tool-tip-${data.diagnosticId}`}
            variant="yellow"
        />
    );
};

const TableEmptyState: FC = () => (
    <Typography color="textDarkSecondary" style={styles.emptyStateDescriptionTitle}>
        No CVT diagnostics have been performed for this vehicle.
    </Typography>
);

const TableHeaderData = memo(({ onSort }: { onSort: (data: GetCVTParams) => void }) => {
    const queryParams = useQuery();
    const sortDirection = queryParams.get('sortDirection');
    const sortBy = queryParams.get('sortBy');

    const HEADER_TITLES = useTableHeaderData();

    return (
        <TableRow>
            {HEADER_TITLES.map(({ key, label, sortable, width }) => (
                <TableHeader
                    testID={key}
                    key={key}
                    width={width}
                    sortable={sortable}
                    sortDirection={sortBy === key ? (sortDirection as SORT_DIRECTION) : null}
                    onSort={(_, sort) => onSort({ sortDirection: sort, sortBy: key })}
                >
                    <Typography style={styles.tableHeader} lineBreakMode="tail" numberOfLines={1}>
                        {label}
                    </Typography>
                </TableHeader>
            ))}
        </TableRow>
    );
});

export const DiagnosticIdCell: React.FC<DiagnosticIdCellProps> = ({ diagnosticId, status, isPending, path }) => {
    const classes = useStyles();
    const isErrorStatus = CVT_STATUS_MAP[status] === 'Failed';

    let content;

    if (isErrorStatus) {
        content = (
            <Link to="#" onClick={(e) => e.preventDefault()} style={{ opacity: 0.5, pointerEvents: 'none' }}>
                {diagnosticId}
            </Link>
        );
    } else if (isPending) {
        content = diagnosticId;
    } else {
        content = (
            <Link
                to={path}
                className={classes.fileCell}
                onClick={() => {
                    analyticsService.logEvent(ANALYTICS_EVENTS.CVT_RESULT_VIEWED, {
                        moduleName: 'cvt',
                        status: status,
                        diagnosticId: diagnosticId,
                    });
                }}
            >
                {diagnosticId}
            </Link>
        );
    }

    return <TableCell>{content}</TableCell>;
};

interface TableRowDataProps {
    data: CVT;
    isDownloading: boolean;
    onDownload: (diagnosticId: string) => void;
}

const TableRowData: FC<TableRowDataProps> = memo(({ data, isDownloading, onDownload }) => {
    const classes = useStyles();
    const { pathname } = useLocation();
    const { deleteCvt } = useCvt(cvtService);

    const handleDownloadButtonClick = useCallback(() => {
        if (isDownloading) {
            return;
        }
        onDownload(data.diagnosticId);
    }, [onDownload, data.diagnosticId, isDownloading]);

    const transformStatus = CVT_STATUS_MAP[data.status];
    const path = `${pathname}/${data.diagnosticId}`;
    const showDownload = [CvtStatus.RECEIVED_RESPONSE_FROM_SBR, CvtStatus.SENT_CVT_TO_SBR].includes(data.status);
    const isPending = transformStatus === 'Pending';

    return (
        <>
            <TableRow>
                <DiagnosticIdCell
                    diagnosticId={data.diagnosticId}
                    status={data.status}
                    isPending={isPending}
                    path={path}
                />
                <TableCell className={classes.badgeCell}>
                    <BadgeComponent data={data} />
                </TableCell>
                <TableCell>
                    <Typography ellipsizeMode="tail" numberOfLines={1}>
                        {data.accountName}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography ellipsizeMode="tail" numberOfLines={1}>
                        {getDisplayDate(data.jobSubmittedTimestamp, DATE_FORMAT.MMM_D_YEAR)}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography ellipsizeMode="tail" numberOfLines={1}>
                        {CVT_STATUS_MAP[data.status]}
                    </Typography>
                </TableCell>
                <TableCell className={classes.actionIcons}>
                    {showDownload &&
                        data?.status !== CvtStatus.SENT_CVT_TO_SBR &&
                        !data?.sbrResponseData?.errorType && (
                            <Button
                                variant="ghost-blue"
                                isLoading={isDownloading}
                                disabled={isDownloading}
                                onPress={handleDownloadButtonClick}
                            >
                                <Icon name="sync-down" color="blueOne" />
                            </Button>
                        )}
                    {(transformStatus === 'Failed' || data?.sbrResponseData?.errorType) && (
                        <Button variant="ghost-blue" onPress={() => deleteCvt(data.diagnosticId)}>
                            <Icon name="trash-striped" color="blueOne" />
                        </Button>
                    )}
                </TableCell>
            </TableRow>
        </>
    );
});

interface CvtTableProps {
    onDownload: (diagnosticId: string) => void;
    downloadingIds: Set<string>;
}

export const CvtTable: FC<CvtTableProps> = ({ onDownload, downloadingIds }) => {
    const classes = useStyles();
    const updateQuery = useUpdateQueryParams();
    const queryParams = useQuery();
    const { state: cvtState } = useCvt(cvtService);

    const { cvtList, loading } = cvtState;
    const page = queryParams.get('page');
    const size = queryParams.get('size');
    const formattedData = Array.isArray(cvtList?.results)
        ? cvtList.results.map((item: CVT) => ({
              ...item,
              rowId: item.diagnosticId || crypto.randomUUID(),
          }))
        : [];

    const handleSortChange = useCallback(
        ({ sortBy, sortDirection }: GetCVTParams) => {
            if (!sortBy || !sortDirection) return;
            updateQuery({ sortBy, sortDirection });
        },
        [updateQuery]
    );

    return (
        <>
            <TableRenderer
                isLoading={loading}
                tableRowsData={formattedData}
                tableRowsDataSetter={() => {}}
                tableHeaderRowRenderer={() => <TableHeaderData onSort={handleSortChange} />}
                tableBodyRowRenderer={(rowData) => (
                    <TableRowData
                        data={rowData}
                        isDownloading={downloadingIds.has(rowData.diagnosticId)}
                        key={rowData.rowId}
                        onDownload={onDownload}
                    />
                )}
                emptyStateProps={{
                    icon: 'auto-cvt',
                    description: <TableEmptyState />,
                    descriptionStyle: styles.typographyStyle,
                    iconProps: { size: 60 },
                }}
                outerTableClassName={formattedData.length ? classes.outerTable : classes.outerTableEmpty}
            />
            <TablePagination
                page={Number(page || 0)}
                size={Number(size || 10)}
                total={cvtList?.totalCount || 0}
                disabled={loading}
                onClick={(pageNumber) => updateQuery({ page: `${pageNumber}` })}
                onSizeChange={(size) => updateQuery({ size: `${size}`, page: '0' })}
            />
        </>
    );
};
